import React, { useState, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import Sidebar from './SideBar';
import useAuthRedirect from './Componentes/auth';
import config from './Componentes/config';
import Swal from 'sweetalert2';
import { getEmpresaByCodigo } from './Componentes/empresasConfig';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';
import * as XLSX from 'xlsx';

const cardImages = {
    DAFA: "/img/dafaLogo.png",
    FUGA: "/img/fugarLogo.png",
    INVE: "/img/idafaLogo.png",
    TRAN: "/img/extasisLogo.png",
    NIDI: "/img/nidiamLogo.png"
};

const PIE_COLORS = ['#4e73df', '#1cc88a', '#f6c23e', '#e74a3b', '#36b9cc', '#fd7e14', '#858796'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const decimalAHHMM = (dec) => {
    const totalMin = Math.round((Number(dec) || 0) * 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

function DashboardAsistencia() {
    useAuthRedirect();
    const [cookies] = useCookies(['selectedCard']);
    const imageUrl = cardImages[cookies.selectedCard];
    const idEmpresa = cookies.idEmpresa;
    const empresaNombre = getEmpresaByCodigo(cookies.selectedCard).nombre;

    // Sidebar collapse states
    const [isComponentsCollapsed, setIsComponentsCollapsed] = useState(true);
    const [isUtilitiesCollapsed, setIsUtilitiesCollapsed] = useState(true);
    const [isInventario, setisIventatioCollapsed] = useState(true);
    const [isPrevencion, setisPrevencionCollapsed] = useState(true);
    const [isLogistica, setisLogisticaCollapsed] = useState(true);
    const [isContabilidadCollapsed, setIsContabilidadCollapsed] = useState(true);
    const [isAdministracionCollapsed, setIsAdministracionCollapsed] = useState(true);
    const toggleCollapse = (setter) => setter(prev => !prev);

    const [mes, setMes] = useState(new Date().getMonth() + 1);
    const [anio, setAnio] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!idEmpresa) return;
        let activo = true;
        setLoading(true);
        fetch(`${config.API_URL}/api/buk/dashboard?empresa=${idEmpresa}&mes=${mes}&anio=${anio}`)
            .then(r => r.json())
            .then(d => { if (activo) setData(d && d.success ? d : null); })
            .catch(() => { if (activo) setData(null); })
            .finally(() => { if (activo) setLoading(false); });
        return () => { activo = false; };
    }, [idEmpresa, mes, anio]);

    const totales = data?.totales || { horasTrab: 0, horasExtras: 0, hnt: 0, inasistencias: 0, diasTrab: 0, trabajadores: 0 };
    const porTrabajador = data?.porTrabajador || [];
    const inasistenciasPorTipo = data?.inasistenciasPorTipo || [];
    const alertas = data?.alertas || [];

    // Top 10 por horas extra para el gráfico
    const topHE = useMemo(() => (
        [...porTrabajador]
            .filter(t => Number(t.horasExtras) > 0)
            .sort((a, b) => Number(b.horasExtras) - Number(a.horasExtras))
            .slice(0, 10)
            .map(t => ({ nombre: (t.nombre || '').split(' ').slice(0, 2).join(' '), HE: Number(t.horasExtras) }))
    ), [porTrabajador]);

    const pieInas = useMemo(() => (
        inasistenciasPorTipo.map(x => ({ name: x.tipo, value: Number(x.cantidad) }))
    ), [inasistenciasPorTipo]);

    const cards = [
        { label: 'Horas trabajadas', valor: decimalAHHMM(totales.horasTrab), color: '#4e73df' },
        { label: 'Horas extra', valor: decimalAHHMM(totales.horasExtras), color: '#1cc88a' },
        { label: 'Horas no trabajadas', valor: decimalAHHMM(totales.hnt), color: '#fd7e14' },
        { label: 'Inasistencias', valor: totales.inasistencias, color: '#e74a3b' },
        { label: 'Trabajadores', valor: totales.trabajadores, color: '#36b9cc' },
    ];

    const exportarExcel = () => {
        if (porTrabajador.length === 0) {
            Swal.fire({ icon: 'info', title: 'Sin datos', text: 'No hay datos para exportar en este período', confirmButtonText: 'Entendido' });
            return;
        }
        const aoa = [];
        aoa.push(['Dashboard de Asistencia']);
        aoa.push(['Empresa', empresaNombre]);
        aoa.push(['Período', `${MESES[mes - 1]} ${anio}`]);
        aoa.push([]);
        aoa.push(['RESUMEN']);
        aoa.push(['Horas trabajadas', decimalAHHMM(totales.horasTrab)]);
        aoa.push(['Horas extra', decimalAHHMM(totales.horasExtras)]);
        aoa.push(['Horas no trabajadas', decimalAHHMM(totales.hnt)]);
        aoa.push(['Inasistencias', totales.inasistencias]);
        aoa.push(['Trabajadores', totales.trabajadores]);
        aoa.push([]);
        aoa.push(['DETALLE POR TRABAJADOR']);
        aoa.push(['Trabajador', 'Días trabajados', 'Horas trabajadas', 'Horas extra', 'Horas no trabajadas', 'Inasistencias']);
        porTrabajador.forEach(t => {
            aoa.push([t.nombre, Number(t.diasTrab) || 0, decimalAHHMM(t.horasTrab), decimalAHHMM(t.horasExtras), decimalAHHMM(t.hnt), Number(t.inasistencias) || 0]);
        });
        aoa.push([]);
        aoa.push(['INASISTENCIAS POR TIPO']);
        aoa.push(['Tipo', 'Cantidad']);
        inasistenciasPorTipo.forEach(x => aoa.push([x.tipo, Number(x.cantidad) || 0]));

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 14 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Dashboard');
        XLSX.writeFile(wb, `Dashboard_Asistencia_${MESES[mes - 1]}_${anio}.xlsx`);
    };

    const selStyle = { padding: '8px', border: '1px solid #ccc', borderRadius: '4px' };
    const chartCard = { background: '#fff', border: '1px solid #e3e6f0', borderRadius: '8px', padding: '16px', boxShadow: '0 0.15rem 1.75rem rgba(58,59,69,.05)' };

    return (
        <body id="page-top">
            <div id="wrapper">
                <Sidebar
                    imageUrl={imageUrl}
                    isComponentsCollapsed={isComponentsCollapsed} setIsComponentsCollapsed={setIsComponentsCollapsed}
                    isUtilitiesCollapsed={isUtilitiesCollapsed} setIsUtilitiesCollapsed={setIsUtilitiesCollapsed}
                    isInventario={isInventario} setisIventatioCollapsed={setisIventatioCollapsed}
                    isPrevencion={isPrevencion} setisPrevencionCollapsed={setisPrevencionCollapsed}
                    isLogistica={isLogistica} setisLogisticaCollapsed={setisLogisticaCollapsed}
                    isContabilidadCollapsed={isContabilidadCollapsed} setIsContabilidadCollapsed={setIsContabilidadCollapsed}
                    isAdministracionCollapsed={isAdministracionCollapsed} setIsAdministracionCollapsed={setIsAdministracionCollapsed}
                    toggleCollapse={toggleCollapse}
                    cookies={cookies}
                />
                <div id="content-wrapper" className="d-flex flex-column">
                    <div id="content">
                        <div className="container-fluid">
                            <div className="card shadow mb-4">
                                <div className="card-header py-3 d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '10px' }}>
                                    <h6 className="m-0 font-weight-bold text-primary">Dashboard de Asistencia — {empresaNombre}</h6>
                                    <div className="d-flex align-items-center" style={{ gap: '8px', flexWrap: 'wrap' }}>
                                        <select value={mes} onChange={e => setMes(Number(e.target.value))} style={selStyle}>
                                            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                        </select>
                                        <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={selStyle}>
                                            {[2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
                                        </select>
                                        <button className="btn btn-outline-success btn-sm" onClick={exportarExcel}>
                                            <i className="fas fa-file-excel"></i> Exportar Excel
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#858796' }}>Cargando dashboard…</div>
                                    ) : porTrabajador.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#858796' }}>
                                            No hay datos de asistencia para {MESES[mes - 1]} {anio}. Sincroniza la empresa primero.
                                        </div>
                                    ) : (
                                        <>
                                            {/* Cards */}
                                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                                {cards.map(c => (
                                                    <div key={c.label} style={{ flex: '1 1 160px', minWidth: '150px', background: '#fff', border: '1px solid #e3e6f0', borderLeft: `4px solid ${c.color}`, borderRadius: '6px', padding: '12px 16px', boxShadow: '0 0.15rem 1.75rem rgba(58,59,69,.05)' }}>
                                                        <div style={{ fontSize: '11px', fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '.5px' }}>{c.label}</div>
                                                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#5a5c69', marginTop: '4px' }}>{c.valor}</div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Alertas */}
                                            {alertas.length > 0 && (
                                                <div style={{ marginBottom: '20px' }}>
                                                    {alertas.map((a, i) => (
                                                        <div key={i} style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107', borderRadius: '4px', padding: '8px 12px', marginBottom: '6px', fontSize: '13px' }}>
                                                            <strong>⚠️ {a.tipo}:</strong> {a.nombre} — {a.mensaje}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Gráficos */}
                                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                                <div style={{ ...chartCard, flex: '2 1 420px', minWidth: '320px' }}>
                                                    <div style={{ fontWeight: 700, color: '#5a5c69', marginBottom: '12px' }}>Horas extra por trabajador (top 10)</div>
                                                    {topHE.length === 0 ? <div style={{ color: '#858796' }}>Sin horas extra en el período.</div> : (
                                                        <ResponsiveContainer width="100%" height={300}>
                                                            <BarChart data={topHE} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                <XAxis dataKey="nombre" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11 }} height={70} />
                                                                <YAxis tick={{ fontSize: 11 }} />
                                                                <Tooltip formatter={(v) => [`${v} h`, 'Horas extra']} />
                                                                <Bar dataKey="HE" fill="#1cc88a" radius={[4, 4, 0, 0]} />
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    )}
                                                </div>
                                                <div style={{ ...chartCard, flex: '1 1 280px', minWidth: '260px' }}>
                                                    <div style={{ fontWeight: 700, color: '#5a5c69', marginBottom: '12px' }}>Inasistencias por tipo</div>
                                                    {pieInas.length === 0 ? <div style={{ color: '#858796' }}>Sin inasistencias en el período.</div> : (
                                                        <ResponsiveContainer width="100%" height={300}>
                                                            <PieChart>
                                                                <Pie data={pieInas} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                                                                    {pieInas.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                                                </Pie>
                                                                <Tooltip />
                                                                <Legend />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Ranking */}
                                            <div style={{ ...chartCard }}>
                                                <div style={{ fontWeight: 700, color: '#5a5c69', marginBottom: '12px' }}>Detalle por trabajador</div>
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table className="table table-sm table-hover" style={{ marginBottom: 0, fontSize: '13px' }}>
                                                        <thead style={{ background: '#f8f9fc' }}>
                                                            <tr>
                                                                <th>Trabajador</th><th>Días trab.</th><th>H. Trabajadas</th><th>H. Extra</th><th>H. No Trab.</th><th>Inasistencias</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {porTrabajador.map(t => (
                                                                <tr key={t.id}>
                                                                    <td>{t.nombre}</td>
                                                                    <td>{Number(t.diasTrab) || 0}</td>
                                                                    <td>{decimalAHHMM(t.horasTrab)}</td>
                                                                    <td style={{ color: Number(t.horasExtras) > 0 ? '#1cc88a' : 'inherit', fontWeight: Number(t.horasExtras) > 0 ? 700 : 400 }}>{decimalAHHMM(t.horasExtras)}</td>
                                                                    <td style={{ color: Number(t.hnt) > 0 ? '#fd7e14' : 'inherit' }}>{decimalAHHMM(t.hnt)}</td>
                                                                    <td style={{ color: Number(t.inasistencias) > 0 ? '#e74a3b' : 'inherit' }}>{Number(t.inasistencias) || 0}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </body>
    );
}

export default DashboardAsistencia;
